/**
 * @file Phone-number matching for search.
 *
 * Phone numbers are NOT a field on /employees — they only exist inside the
 * Bamboo SIM assets (`assets[]` where category is "Sim Card"), and they're
 * stored inconsistently: usually in `description` ("+420 774 833 785"),
 * sometimes in `serial` (which otherwise holds the ICCID). That's the same
 * mess we hit reconciling the T-Mobile billing export, so matching is done on
 * bare digits rather than the literal string.
 *
 * Normalisation drops every non-digit AND a leading Czech country code, so
 * all of these find the same line:
 *
 *   "+420 774 833 785"   "420774833785"   "774 833 785"   "774833785"
 *
 * Partial matches work too (substring on digits), which is what makes
 * "who holds the number ending 3785?" answerable.
 */

/** Shortest digit run we treat as a number search — below this, a query like
 *  an employee number ("914") would smear across unrelated numbers. The
 *  text haystack already covers short numeric fields. */
export const MIN_PHONE_DIGITS = 4;

/**
 * Is this query a phone number at all? Only digits and phone punctuation
 * (+, spaces, dashes, dots, slashes, brackets). A serial like "F3XK9J2X9W"
 * is NOT — its digits "3929" matched an old SIM number (739 294 467) and
 * pulled an unrelated person into the results.
 */
export function looksLikePhoneQuery(value) {
  const q = String(value ?? '').trim();
  return /^[+\d\s()./-]+$/.test(q) && phoneDigits(q).length >= MIN_PHONE_DIGITS;
}

/** Bare digits, with a leading 420 country code stripped. */
export function phoneDigits(value) {
  const digits = String(value ?? '').replace(/\D+/g, '');
  return digits.length > 9 && digits.startsWith('420') ? digits.slice(3) : digits;
}

function isSimAsset(asset) {
  return String(asset?.category || '').toLowerCase().includes('sim');
}

/**
 * Every number-ish string on an employee's SIM assets.
 * @returns [{ raw, digits, active }] — `active` is false once Finance has
 *   filled Date returned in Bamboo (kept, because "who used to hold this
 *   number we're still being billed for?" is exactly the question asked).
 */
export function employeeSimNumbers(employee) {
  const out = [];
  for (const asset of (employee?.assets || [])) {
    if (!isSimAsset(asset)) continue;
    for (const raw of [asset.description, asset.serial]) {
      if (!raw) continue;
      const digits = phoneDigits(raw);
      if (digits.length < MIN_PHONE_DIGITS) continue;
      if (out.some((n) => n.digits === digits)) continue; // number in both fields
      out.push({ raw: String(raw).trim(), digits, active: !asset.dateReturned });
    }
  }
  return out;
}

/**
 * Does this employee hold a number matching the (already normalised) query
 * digits? Returns the matched number for display, or null.
 */
export function matchSimNumber(employee, queryDigits) {
  if (!queryDigits || queryDigits.length < MIN_PHONE_DIGITS) return null;
  for (const n of employeeSimNumbers(employee)) {
    if (n.digits.includes(queryDigits)) return n;
  }
  return null;
}
